import * as DocumentPicker from 'expo-document-picker';
import { File } from 'expo-file-system';
import { CheckIcon, FileUpIcon, MapPinIcon, RouteIcon, Share2Icon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, TextInput, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { RoutePreview } from '@/components/routes/route-preview';
import { shareRoute } from '@/lib/share';
import { createGpxRoute, fetchTenantRoutes, type RouteLibraryItem } from '@/lib/routes';
import { BRAND } from '@sixam/config/brand';
import {
  formatRouteDistance,
  MAX_GPX_FILE_BYTES,
  parseGpxRoute,
  routePreviewPath,
  type RouteLineString,
  type ParsedGpxRoute,
} from '@sixam/shared/route-core';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { cn } from '@sixam/ui/utils';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

type RouteSelectorProps = Readonly<{
  initialItems?: readonly RouteLibraryItem[];
  mode?: 'attach' | 'library';
  onSelect?: (item: RouteLibraryItem | null) => void;
  query?: string;
  selectedVersionId?: string | null;
  tenantId: string;
}>;

type GpxDraft = Readonly<{
  fileName: string;
  name: string;
  parsed: ParsedGpxRoute;
}>;

function selectedVersionForMode(
  mode: 'attach' | 'library',
  librarySelectedVersionId: string | null,
  selectedVersionId: string | null
) {
  return mode === 'library' ? librarySelectedVersionId : selectedVersionId;
}

function filterRouteItems(items: RouteLibraryItem[], query: string) {
  if (!query) return items;
  return items.filter((item) =>
    [item.route.name, item.route.description].some((value) => value?.toLowerCase().includes(query))
  );
}

function routeSelectorCopy(mode: 'attach' | 'library') {
  if (mode === 'library') {
    return {
      detail: 'Import a GPX to save a reusable club route.',
      label: 'Saved routes',
    };
  }
  return {
    detail: 'Import a GPX once, then reuse it across club events.',
    label: 'Route library',
  };
}

function routeSaveLabel(isSaving: boolean, mode: 'attach' | 'library') {
  if (isSaving) return 'Saving…';
  return mode === 'library' ? 'Save route' : 'Save & attach';
}

function routeItemAccessibilityLabel(mode: 'attach' | 'library', selected: boolean, name: string) {
  if (mode === 'library') return `View ${name}`;
  return `${selected ? 'Selected' : 'Select'} ${name}`;
}

function formatCoordinate(value: number, positive: string, negative: string) {
  return `${Math.abs(value).toFixed(3)}° ${value >= 0 ? positive : negative}`;
}

function routeStartLabel(geometry: RouteLineString) {
  const [longitude, latitude] = geometry.coordinates[0] ?? [];
  if (latitude == null || longitude == null) return 'Start location unavailable';
  return `${formatCoordinate(latitude, 'N', 'S')} · ${formatCoordinate(longitude, 'E', 'W')}`;
}

// Route selection composes independently releasable import, preview, and sharing capabilities.
// eslint-disable-next-line sonarjs/cognitive-complexity
export function RouteSelector({
  initialItems,
  mode = 'attach',
  onSelect,
  query = '',
  selectedVersionId = null,
  tenantId,
}: RouteSelectorProps) {
  const gpxImportEnabled = useReleaseFeatureFlag('gpxImport') === true;
  const routePreviewsEnabled = useReleaseFeatureFlag('routePreviews') === true;
  const routeSharingEnabled = useReleaseFeatureFlag('routeSharing') === true;
  const [items, setItems] = React.useState<RouteLibraryItem[]>([...(initialItems ?? [])]);
  const [isLoading, setIsLoading] = React.useState(initialItems === undefined);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<GpxDraft | null>(null);
  const [draftName, setDraftName] = React.useState('');
  const [librarySelectedVersionId, setLibrarySelectedVersionId] = React.useState<string | null>(
    null
  );
  const effectiveSelectedVersionId = selectedVersionForMode(
    mode,
    librarySelectedVersionId,
    selectedVersionId
  );
  const normalizedQuery = query.trim().toLowerCase();
  const visibleItems = filterRouteItems(items, normalizedQuery);
  const selectedItem = items.find((item) => item.version.id === effectiveSelectedVersionId);
  const copy = routeSelectorCopy(mode);

  const loadItems = React.useCallback(async () => {
    if (initialItems !== undefined) {
      setItems([...initialItems]);
      setIsLoading(false);
      return [...initialItems];
    }
    setIsLoading(true);
    try {
      const next = await fetchTenantRoutes(tenantId);
      setItems(next);
      setError(null);
      return next;
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not load route library.');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [initialItems, tenantId]);

  React.useEffect(() => {
    if (initialItems !== undefined) return;
    let active = true;
    void fetchTenantRoutes(tenantId)
      .then((next) => {
        if (!active) return;
        setItems(next);
        setError(null);
        setIsLoading(false);
      })
      .catch((loadError: unknown) => {
        if (!active) return;
        setError(loadError instanceof Error ? loadError.message : 'Could not load route library.');
        setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [initialItems, tenantId]);

  async function chooseGpx() {
    setError(null);
    const result = await DocumentPicker.getDocumentAsync({
      copyToCacheDirectory: true,
      multiple: false,
      type: ['application/gpx+xml', 'application/xml', 'text/xml', 'application/octet-stream'],
    });
    if (result.canceled) return;

    const asset = result.assets[0];
    if (asset.size != null && asset.size > MAX_GPX_FILE_BYTES) {
      setError('GPX files must be 5 MB or smaller.');
      return;
    }

    try {
      const xml = await new File(asset.uri).text();
      if (new TextEncoder().encode(xml).length > MAX_GPX_FILE_BYTES) {
        throw new Error('GPX files must be 5 MB or smaller.');
      }
      const parsed = parseGpxRoute(xml);
      const fallbackName = asset.name
        .replace(/\.gpx$/i, '')
        .replaceAll(/[-_]+/g, ' ')
        .trim();
      const name = parsed.name || fallbackName || 'Morning route';
      setDraft({ fileName: asset.name, name, parsed });
      setDraftName(name);
    } catch (parseError) {
      setError(parseError instanceof Error ? parseError.message : 'Could not read that GPX file.');
    }
  }

  async function saveDraft() {
    if (!draft || !draftName.trim()) {
      setError('Give this route a name.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const versionId = await createGpxRoute({
        fileName: draft.fileName,
        name: draftName,
        parsed: draft.parsed,
        tenantId,
      });
      const next = await loadItems();
      const saved = next.find((item) => item.version.id === versionId);
      if (!saved) throw new Error('The route saved, but could not be reloaded.');
      if (mode === 'library') setLibrarySelectedVersionId(saved.version.id);
      onSelect?.(saved);
      setDraft(null);
      setDraftName('');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not save this route.');
    } finally {
      setIsSaving(false);
    }
  }

  if (draft) {
    return (
      <View className="gap-5 py-4">
        {routePreviewsEnabled ? (
          <RoutePreview
            distanceM={draft.parsed.distanceM}
            elevationGainM={draft.parsed.elevationGainM}
            geometry={draft.parsed.geometry}
            name={draftName || draft.name}
          />
        ) : null}
        <View className="gap-2">
          <Text variant="label">Route name</Text>
          <TextInput
            accessibilityLabel="Route name"
            className="min-h-12 border-b border-border px-1 font-sans text-base text-foreground outline-none"
            maxLength={120}
            onChangeText={setDraftName}
            placeholder="Morning loop"
            placeholderTextColor="#71717A"
            value={draftName}
          />
          <Text variant="muted">
            {draft.fileName} · {formatRouteDistance(draft.parsed.distanceM)} ·{' '}
            {draft.parsed.originalPointCount} points
          </Text>
        </View>
        {error ? <Text className="text-sm text-destructive">{error}</Text> : null}
        <View className="flex-row gap-3">
          <Button className="flex-1" disabled={isSaving} onPress={() => void saveDraft()}>
            {isSaving ? (
              <ActivityIndicator color="white" />
            ) : (
              <Icon as={CheckIcon} className="size-4" />
            )}
            <Text>{routeSaveLabel(isSaving, mode)}</Text>
          </Button>
          <Button
            disabled={isSaving}
            onPress={() => {
              setDraft(null);
              setError(null);
            }}
            variant="secondary"
          >
            <Text>Cancel</Text>
          </Button>
        </View>
      </View>
    );
  }

  return (
    <View className="gap-5 py-4">
      <View className="flex-row items-center justify-between gap-4">
        <View className="min-w-0 flex-1 gap-1">
          <Text variant="label">{copy.label}</Text>
          <Text variant="muted">{copy.detail}</Text>
        </View>
        {gpxImportEnabled ? (
          <Button accessibilityLabel="Import GPX route" onPress={() => void chooseGpx()} size="sm">
            <Icon as={FileUpIcon} className="size-4" />
            <Text>Import GPX</Text>
          </Button>
        ) : null}
      </View>

      {error ? <Text className="text-sm text-destructive">{error}</Text> : null}
      {selectedItem && mode === 'library' ? (
        <View className="gap-3">
          {routePreviewsEnabled ? (
            <RoutePreview
              distanceM={selectedItem.version.distance_m}
              elevationGainM={selectedItem.version.elevation_gain_m}
              geometry={selectedItem.version.geometry}
              name={selectedItem.route.name}
            />
          ) : null}
          {routeSharingEnabled && selectedItem.route.share_enabled ? (
            <Button
              onPress={() =>
                void shareRoute({
                  name: selectedItem.route.name,
                  shareToken: selectedItem.route.share_token,
                })
              }
              variant="secondary"
            >
              <Icon as={Share2Icon} className="size-4" />
              <Text>Share route</Text>
            </Button>
          ) : null}
        </View>
      ) : null}
      <RouteLibraryItems
        isLoading={isLoading}
        items={visibleItems}
        mode={mode}
        onSelect={(item) => {
          if (mode === 'library') setLibrarySelectedVersionId(item.version.id);
          onSelect?.(item);
        }}
        query={normalizedQuery}
        selectedVersionId={effectiveSelectedVersionId}
      />

      {effectiveSelectedVersionId && mode === 'attach' ? (
        <Button onPress={() => onSelect?.(null)} variant="secondary">
          <Text>Remove route</Text>
        </Button>
      ) : null}
    </View>
  );
}

function RouteLibraryItems({
  isLoading,
  items,
  mode,
  onSelect,
  query,
  selectedVersionId,
}: Readonly<{
  isLoading: boolean;
  items: readonly RouteLibraryItem[];
  mode: 'attach' | 'library';
  onSelect: (item: RouteLibraryItem) => void;
  query: string;
  selectedVersionId: string | null;
}>) {
  if (isLoading) {
    return (
      <View className="items-center gap-3 py-12">
        <ActivityIndicator />
        <Text variant="muted">Loading routes…</Text>
      </View>
    );
  }

  if (items.length === 0) {
    return (
      <View className="items-center gap-3 border-y border-border py-12">
        <Icon as={RouteIcon} className="size-7 text-muted-foreground" />
        <View className="items-center gap-1 px-6">
          <Text variant="large">{query ? 'No routes match' : 'No saved routes yet'}</Text>
          <Text variant="muted" className="text-center">
            {query
              ? 'Try a different route name.'
              : 'Export a GPX from Plotaroute and import it here.'}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View>
      {items.map((item) => {
        const selected = item.version.id === selectedVersionId;
        return (
          <Pressable
            key={item.version.id}
            accessibilityLabel={routeItemAccessibilityLabel(mode, selected, item.route.name)}
            accessibilityRole={mode === 'library' ? 'button' : 'radio'}
            accessibilityState={mode === 'library' ? undefined : { checked: selected }}
            className={cn(
              'flex-row items-center gap-3 border-b border-border py-4 active:opacity-70',
              selected && 'bg-accent'
            )}
            onPress={() => onSelect(item)}
          >
            <RouteLibraryThumbnail geometry={item.version.geometry} name={item.route.name} />
            <View className="min-w-0 flex-1 gap-1">
              <Text variant="large" numberOfLines={1}>
                {item.route.name}
              </Text>
              <Text variant="mono">{formatRouteDistance(item.version.distance_m)}</Text>
              <View className="flex-row items-center gap-1">
                <Icon as={MapPinIcon} className="size-3 text-muted-foreground" />
                <Text variant="muted" className="text-xs" numberOfLines={1}>
                  {routeStartLabel(item.version.geometry)}
                </Text>
              </View>
            </View>
            {selected ? (
              <View className="size-8 items-center justify-center rounded-full bg-primary">
                <Icon as={CheckIcon} className="size-4 text-primary-foreground" />
              </View>
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
}

function RouteLibraryThumbnail({
  geometry,
  name,
}: Readonly<{ geometry: RouteLineString; name: string }>) {
  const path = routePreviewPath(geometry.coordinates, 120, 80, 12);

  return (
    <View
      className="h-16 w-24 overflow-hidden rounded-xl border border-border bg-card"
      style={{ borderCurve: 'continuous' }}
    >
      <Svg
        accessibilityLabel={`Route shape for ${name}`}
        accessibilityRole="image"
        height="100%"
        viewBox="0 0 120 80"
        width="100%"
      >
        <Path
          d={path}
          fill="none"
          opacity={0.18}
          stroke={BRAND.color.orange}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={12}
        />
        <Path
          d={path}
          fill="none"
          stroke={BRAND.color.orange}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={4}
        />
      </Svg>
    </View>
  );
}
